import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { 
  Share2, Plus, X, Send, Image, Video, Link2, Trash2,
  Crown, Lock, Check, AlertCircle, ExternalLink, History,
  BarChart3, Settings, RefreshCw, Loader2, CheckCircle2,
  Twitter, Facebook, Instagram, Linkedin, Clock, MapPin
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Platform icons mapping
const PlatformIcon = ({ platform, className = "w-5 h-5" }) => {
  const icons = {
    twitter: <Twitter className={className} />,
    facebook: <Facebook className={className} />,
    instagram: <Instagram className={className} />,
    linkedin: <Linkedin className={className} />,
    tiktok: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    ),
    threads: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.59 12c.025 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.333-3.022.812-.672 1.927-1.09 3.22-1.208.89-.082 1.778-.058 2.668.072l.008-.428c-.013-.9-.072-1.94-.46-2.77-.37-.79-1.092-1.357-2.147-1.69l.558-1.942c1.528.482 2.59 1.347 3.157 2.57.52 1.123.603 2.467.622 3.623.004.226.005.45.002.67a9.594 9.594 0 0 1 2.18 1.107c1.947 1.282 2.382 3.055 2.382 4.313 0 1.108-.283 2.312-.834 3.57-.938 2.14-2.852 3.71-5.691 4.67-1.452.49-3.047.761-4.748.806-.214.006-.43.009-.645.009z"/>
      </svg>
    ),
    bluesky: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/>
      </svg>
    )
  };
  return icons[platform] || <Share2 className={className} />;
};

const HubPost = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('compose');
  const [connections, setConnections] = useState([]);
  const [platforms, setPlatforms] = useState({});
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Compose state
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [includeSignature, setIncludeSignature] = useState(true);
  const [posting, setPosting] = useState(false);
  
  // Schedule state
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduledPosts, setScheduledPosts] = useState([]);
  
  // Locale integration state (when Locale platform is selected)
  const [localeTitle, setLocaleTitle] = useState('');
  const [localeType, setLocaleType] = useState('deal');
  const [localeAddress, setLocaleAddress] = useState('');
  const [localeCategory, setLocaleCategory] = useState('');
  
  // Connect modal
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [manualConnect, setManualConnect] = useState({ username: '', accountName: '' });
  
  // Check access
  const allowedTiers = ['enterprise', 'supremium', 'enterprise_supremium', 'nonprofit_enterprise', 'nonprofit_supremium'];
  const hasAccess = allowedTiers.includes(user?.subscription_tier) || user?.is_admin;
  
  useEffect(() => {
    if (hasAccess) {
      fetchData();
    }
  }, [hasAccess]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [platformsRes, connectionsRes, statsRes, historyRes, scheduledRes] = await Promise.all([
        axios.get(`${API_URL}/hubpost/platforms`, { withCredentials: true }),
        axios.get(`${API_URL}/hubpost/connections`, { withCredentials: true }),
        axios.get(`${API_URL}/hubpost/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/hubpost/history?limit=10`, { withCredentials: true }),
        axios.get(`${API_URL}/hubpost/scheduled`, { withCredentials: true })
      ]);
      
      setPlatforms(platformsRes.data.platforms || {});
      setConnections(connectionsRes.data.connections || []);
      setStats(statsRes.data);
      setHistory(historyRes.data.crossposts || []);
      setScheduledPosts(scheduledRes.data.scheduled_posts || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Failed to load HubPost data');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const connectPlatform = async (platform) => {
    try {
      await axios.post(`${API_URL}/hubpost/connect`, {
        platform,
        account_username: manualConnect.username || `@user_${platform}`,
        account_name: manualConnect.accountName || `${platforms[platform]?.name} Account`
      }, { withCredentials: true });
      
      toast.success(`Connected to ${platforms[platform]?.name}!`);
      setShowConnectModal(false);
      setConnectingPlatform(null);
      setManualConnect({ username: '', accountName: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect platform');
    }
  };
  
  const disconnectPlatform = async (connectionId, platformName) => {
    if (!confirm(`Disconnect ${platformName}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/hubpost/connections/${connectionId}`, { withCredentials: true });
      toast.success('Platform disconnected');
      fetchData();
    } catch (error) {
      toast.error('Failed to disconnect platform');
    }
  };
  
  const togglePlatform = (platform) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };
  
  const crossPost = async () => {
    if (!content.trim()) {
      toast.error('Please enter some content');
      return;
    }
    if (selectedPlatforms.length === 0) {
      toast.error('Select at least one platform');
      return;
    }
    
    // Validate schedule if in schedule mode
    if (scheduleMode) {
      if (!scheduleDate || !scheduleTime) {
        toast.error('Please select both date and time for scheduling');
        return;
      }
      const scheduleDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
      if (scheduleDateTime <= new Date()) {
        toast.error('Schedule time must be in the future');
        return;
      }
    }
    
    setPosting(true);
    try {
      const payload = {
        content,
        images,
        platforms: selectedPlatforms,
        include_signature: includeSignature
      };
      
      // Add schedule time if scheduling
      if (scheduleMode && scheduleDate && scheduleTime) {
        payload.schedule_time = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      }
      
      // Add Locale data if Locale is in selected platforms
      if (selectedPlatforms.includes('locale')) {
        if (!localeAddress) {
          toast.error('Address is required for Locale posts');
          setPosting(false);
          return;
        }
        payload.locale_title = localeTitle || content.substring(0, 100);
        payload.locale_type = localeType;
        payload.locale_address = localeAddress;
        payload.locale_category = localeCategory;
        // Default coordinates (would normally come from address geocoding)
        payload.locale_latitude = 40.7128;
        payload.locale_longitude = -74.0060;
      }
      
      const response = await axios.post(`${API_URL}/hubpost/crosspost`, payload, { withCredentials: true });
      
      if (scheduleMode) {
        toast.success(`Post scheduled for ${scheduleDate} at ${scheduleTime}!`);
      } else {
        toast.success(`Posted to ${response.data.platforms_posted} platform(s)!`);
      }
      
      setContent('');
      setImages([]);
      setSelectedPlatforms([]);
      setScheduleMode(false);
      setScheduleDate('');
      setScheduleTime('');
      setLocaleTitle('');
      setLocaleAddress('');
      setLocaleCategory('');
      setLocaleType('deal');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cross-post');
    } finally {
      setPosting(false);
    }
  };
  
  const cancelScheduledPost = async (crosspostId) => {
    if (!confirm('Cancel this scheduled post?')) return;
    
    try {
      await axios.delete(`${API_URL}/hubpost/scheduled/${crosspostId}`, { withCredentials: true });
      toast.success('Scheduled post cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel scheduled post');
    }
  };
  
  const publishNow = async (crosspostId) => {
    try {
      const response = await axios.post(`${API_URL}/hubpost/scheduled/${crosspostId}/publish`, {}, { withCredentials: true });
      toast.success(`Published to ${response.data.platforms_posted} platform(s)!`);
      fetchData();
    } catch (error) {
      toast.error('Failed to publish post');
    }
  };
  
  // Get connected platform names
  const connectedPlatformIds = connections.map(c => c.platform);
  
  // Character count preview
  const previewContent = includeSignature 
    ? content + '\n\n— created with HubPost from Socyy™' 
    : content;
  
  // OAuth connection handler
  const initiateOAuth = async (platform) => {
    setConnectingPlatform(platform);
    try {
      const response = await axios.get(`${API_URL}/hubpost/oauth/authorize/${platform}`, { withCredentials: true });
      
      if (response.data.auth_url) {
        // Store state in sessionStorage for CSRF protection
        sessionStorage.setItem('oauth_state', response.data.state);
        if (response.data.code_verifier) {
          sessionStorage.setItem('oauth_code_verifier', response.data.code_verifier);
        }
        // Redirect to OAuth provider
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start OAuth flow');
      setConnectingPlatform(null);
    }
  };
  
  // Handle OAuth callback (check URL params on load)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const platform = urlParams.get('platform');
    
    if (code && state && platform) {
      handleOAuthCallback(code, state, platform);
    }
  }, []);
  
  const handleOAuthCallback = async (code, state, platform) => {
    // Verify state for CSRF protection
    const storedState = sessionStorage.getItem('oauth_state');
    if (state !== storedState) {
      toast.error('Invalid OAuth state - possible CSRF attack');
      return;
    }
    
    const codeVerifier = sessionStorage.getItem('oauth_code_verifier');
    
    try {
      setLoading(true);
      await axios.post(`${API_URL}/hubpost/oauth/callback`, {
        code,
        state,
        platform,
        code_verifier: codeVerifier
      }, { withCredentials: true });
      
      // Clean up
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('oauth_code_verifier');
      
      // Remove URL params
      window.history.replaceState({}, document.title, window.location.pathname);
      
      toast.success(`Connected to ${platform}!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete OAuth');
    } finally {
      setLoading(false);
    }
  };
  
  // Show OAuth status message
  const getOAuthStatus = (platform) => {
    const platformInfo = platforms[platform];
    if (!platformInfo?.requires_oauth) return null;
    
    if (!platformInfo.client_configured) {
      return (
        <p className="text-xs text-amber-600 mt-1">
          OAuth not configured - contact admin
        </p>
      );
    }
    return null;
  };

  // For users without access, show upgrade page
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center mx-auto mb-6">
              <Share2 className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-heading font-black text-gray-900 dark:text-white mb-4">
              HubPost
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-2">
              Cross-post to all your social platforms from one place
            </p>
            <p className="text-lg text-violet-600 font-semibold mb-8">
              Available for <span className="text-fuchsia-600">Business</span> and <span className="text-amber-500">Supremium</span> users
            </p>
            
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-violet-200 dark:border-violet-800 mb-8">
              <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">HubPost Features:</h3>
              <ul className="space-y-3 text-left max-w-md mx-auto">
                {[
                  'Post to Twitter, Facebook, Instagram, LinkedIn, TikTok & more',
                  'Automatic "created with HubPost from Socyy™" branding',
                  'Smart content adaptation per platform',
                  'Posting history & analytics',
                  'Schedule posts for later',
                  'OAuth secure connections'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                    <Check className="w-5 h-5 text-violet-500" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <div className="flex justify-center gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                {['twitter', 'facebook', 'instagram', 'linkedin', 'tiktok', 'threads', 'bluesky'].map(p => (
                  <div key={p} className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <PlatformIcon platform={p} className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </div>
                ))}
              </div>
            </div>
            
            <Button
              onClick={() => navigate('/pricing')}
              className="rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-bold px-8 h-14 text-lg hover:scale-105 transition-transform"
              data-testid="upgrade-for-hubpost-btn"
            >
              <Crown className="w-5 h-5 mr-2" />
              Upgrade to Business - $29.99/month
            </Button>
          </motion.div>
        </div>
        <FloatingDock user={user} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-violet-200 dark:border-violet-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Share2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  HubPost
                  <Badge className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-xs">
                    {user?.subscription_tier === 'supremium' ? 'Supremium' : 'Business'}
                  </Badge>
                </h1>
                <p className="text-sm text-gray-500">{connections.length} platform(s) connected</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 pb-24">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="text-2xl font-bold text-violet-600">{stats.connected_platforms}</div>
              <div className="text-sm text-gray-500">Connected</div>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="text-2xl font-bold text-fuchsia-600">{stats.total_crossposts}</div>
              <div className="text-sm text-gray-500">Cross-posts</div>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="text-2xl font-bold text-blue-600">{stats.available_platforms}</div>
              <div className="text-sm text-gray-500">Available</div>
            </Card>
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <div className="text-2xl font-bold text-green-600">
                {stats.platform_stats?.reduce((acc, p) => acc + (p.posts_count || 0), 0) || 0}
              </div>
              <div className="text-sm text-gray-500">Total Posts</div>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-violet-100 dark:bg-gray-800 rounded-2xl p-1">
            <TabsTrigger value="compose" className="rounded-xl data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Send className="w-4 h-4 mr-2" />
              Compose
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="rounded-xl data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Clock className="w-4 h-4 mr-2" />
              Scheduled
              {scheduledPosts.length > 0 && (
                <Badge className="ml-2 bg-violet-500 text-white text-xs">{scheduledPosts.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="platforms" className="rounded-xl data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Settings className="w-4 h-4 mr-2" />
              Platforms
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-xl data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <History className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Compose Tab */}
          <TabsContent value="compose" className="space-y-6">
            <Card className="p-6 bg-white dark:bg-gray-800 rounded-3xl">
              <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Create Cross-Post</h3>
              
              {/* Content Input */}
              <div className="space-y-4">
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="What's on your mind? This will be posted to all selected platforms..."
                  className="min-h-[150px] rounded-xl resize-none"
                  data-testid="hubpost-content-input"
                />
                
                {/* Character counts per platform */}
                {content && (
                  <div className="flex flex-wrap gap-2">
                    {selectedPlatforms.map(p => {
                      const platform = platforms[p];
                      const maxChars = platform?.max_chars || 280;
                      const isOver = previewContent.length > maxChars;
                      return (
                        <Badge 
                          key={p} 
                          variant="outline" 
                          className={`${isOver ? 'border-red-500 text-red-500' : 'border-gray-300'}`}
                        >
                          {platform?.name}: {previewContent.length}/{maxChars}
                        </Badge>
                      );
                    })}
                  </div>
                )}
                
                {/* Signature toggle */}
                <div className="flex items-center justify-between p-4 bg-violet-50 dark:bg-violet-900/20 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-violet-500" />
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">Add HubPost Signature</p>
                      <p className="text-sm text-gray-500">— created with HubPost from Socyy™</p>
                    </div>
                  </div>
                  <Switch
                    checked={includeSignature}
                    onCheckedChange={setIncludeSignature}
                    data-testid="signature-toggle"
                  />
                </div>
                
                {/* Platform Selection */}
                <div>
                  <p className="font-semibold mb-3 text-gray-900 dark:text-white">Select Platforms to Post:</p>
                  {connections.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                      <Link2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">No platforms connected yet</p>
                      <Button
                        variant="link"
                        onClick={() => setActiveTab('platforms')}
                        className="text-violet-600"
                      >
                        Connect your first platform
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {connections.map(conn => {
                        const isSelected = selectedPlatforms.includes(conn.platform);
                        return (
                          <button
                            key={conn.connection_id}
                            onClick={() => togglePlatform(conn.platform)}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              isSelected 
                                ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30' 
                                : 'border-gray-200 dark:border-gray-700 hover:border-violet-300'
                            }`}
                            data-testid={`select-platform-${conn.platform}`}
                          >
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded-xl flex items-center justify-center"
                                style={{ backgroundColor: `${conn.platform_color}20` }}
                              >
                                <PlatformIcon 
                                  platform={conn.platform} 
                                  className="w-5 h-5"
                                  style={{ color: conn.platform_color }}
                                />
                              </div>
                              <div className="text-left">
                                <p className="font-semibold text-gray-900 dark:text-white">{conn.platform_name}</p>
                                <p className="text-xs text-gray-500">{conn.account_username}</p>
                              </div>
                              {isSelected && (
                                <CheckCircle2 className="w-5 h-5 text-violet-500 ml-auto" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Locale Options - shown when Locale platform is selected */}
                {selectedPlatforms.includes('locale') && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl space-y-4 border-2 border-green-300 dark:border-green-700">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                      <MapPin className="w-5 h-5" />
                      <span className="font-semibold">Locale Post Settings</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Post Type</label>
                        <select
                          value={localeType}
                          onChange={(e) => setLocaleType(e.target.value)}
                          className="w-full p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                          data-testid="locale-type-select"
                        >
                          <option value="deal">Deal</option>
                          <option value="special">Special</option>
                          <option value="event">Event</option>
                          <option value="show">Show</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Category</label>
                        <select
                          value={localeCategory}
                          onChange={(e) => setLocaleCategory(e.target.value)}
                          className="w-full p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                          data-testid="locale-category-select"
                        >
                          <option value="">Select Category</option>
                          <option value="food">Food & Dining</option>
                          <option value="shopping">Shopping</option>
                          <option value="entertainment">Entertainment</option>
                          <option value="nightlife">Nightlife</option>
                          <option value="services">Services</option>
                          <option value="health">Health & Wellness</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Title (optional)</label>
                      <Input
                        value={localeTitle}
                        onChange={(e) => setLocaleTitle(e.target.value)}
                        placeholder="Custom title for Locale (uses content if empty)"
                        className="rounded-xl"
                        data-testid="locale-title-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={localeAddress}
                        onChange={(e) => setLocaleAddress(e.target.value)}
                        placeholder="123 Main St, City, State"
                        className="rounded-xl"
                        data-testid="locale-address-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Required for Locale posts to appear on the map</p>
                    </div>
                  </div>
                )}
                
                {/* Schedule Toggle */}
                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">Schedule for Later</p>
                      <p className="text-sm text-gray-500">Post at a specific date and time</p>
                    </div>
                  </div>
                  <Switch
                    checked={scheduleMode}
                    onCheckedChange={setScheduleMode}
                    data-testid="schedule-toggle"
                  />
                </div>
                
                {/* Schedule Date/Time Picker */}
                {scheduleMode && (
                  <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Date</label>
                      <Input
                        type="date"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="rounded-xl"
                        data-testid="schedule-date-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Time</label>
                      <Input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        className="rounded-xl"
                        data-testid="schedule-time-input"
                      />
                    </div>
                  </div>
                )}
                
                {/* Post Button */}
                <Button
                  onClick={crossPost}
                  disabled={posting || !content.trim() || selectedPlatforms.length === 0}
                  className={`w-full h-14 rounded-2xl font-bold text-lg ${
                    scheduleMode 
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white' 
                      : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white'
                  }`}
                  data-testid="crosspost-submit-btn"
                >
                  {posting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {scheduleMode ? 'Scheduling...' : `Posting to ${selectedPlatforms.length} platform(s)...`}
                    </>
                  ) : scheduleMode ? (
                    <>
                      <Clock className="w-5 h-5 mr-2" />
                      Schedule for {scheduleDate && scheduleTime ? `${scheduleDate} at ${scheduleTime}` : 'Selected Time'}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Cross-Post to {selectedPlatforms.length || 'Selected'} Platform(s)
                    </>
                  )}
                </Button>
              </div>
            </Card>

            {/* Preview */}
            {content && (
              <Card className="p-6 bg-white dark:bg-gray-800 rounded-3xl">
                <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Preview</h3>
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                  {previewContent}
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Scheduled Tab */}
          <TabsContent value="scheduled" className="space-y-6">
            <Card className="p-6 bg-white dark:bg-gray-800 rounded-3xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-lg text-gray-900 dark:text-white">Scheduled Posts</h3>
                <Button variant="ghost" onClick={fetchData} className="rounded-full">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              
              {scheduledPosts.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h4 className="font-bold text-xl text-gray-900 dark:text-white mb-2">No Scheduled Posts</h4>
                  <p className="text-gray-500 mb-4">Schedule posts to go out at specific times</p>
                  <Button
                    onClick={() => {
                      setScheduleMode(true);
                      setActiveTab('compose');
                    }}
                    className="rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Schedule Your First Post
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {scheduledPosts.map(post => (
                    <div 
                      key={post.crosspost_id}
                      className="p-4 rounded-xl border-2 border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <p className="text-gray-900 dark:text-white line-clamp-2">{post.original_content}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                              <Clock className="w-3 h-3 mr-1" />
                              {new Date(post.schedule_time).toLocaleString()}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.platforms?.map(p => (
                          <div 
                            key={p}
                            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white dark:bg-gray-800"
                          >
                            <PlatformIcon platform={p} className="w-3 h-3" />
                            <span className="text-xs">{platforms[p]?.name || p}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => publishNow(post.crosspost_id)}
                          className="rounded-full bg-green-500 text-white hover:bg-green-600"
                          data-testid={`publish-now-${post.crosspost_id}`}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Publish Now
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => cancelScheduledPost(post.crosspost_id)}
                          className="rounded-full text-red-500 border-red-300 hover:bg-red-50"
                          data-testid={`cancel-scheduled-${post.crosspost_id}`}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Platforms Tab */}
          <TabsContent value="platforms" className="space-y-6">
            <Card className="p-6 bg-white dark:bg-gray-800 rounded-3xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-lg text-gray-900 dark:text-white">Connected Platforms</h3>
                <Button
                  onClick={() => setShowConnectModal(true)}
                  className="rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white"
                  data-testid="add-platform-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Connect Platform
                </Button>
              </div>
              
              {loading ? (
                <div className="space-y-4">
                  {[1, 2].map(i => (
                    <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : connections.length === 0 ? (
                <div className="text-center py-12">
                  <Link2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h4 className="font-bold text-xl text-gray-900 dark:text-white mb-2">No Platforms Connected</h4>
                  <p className="text-gray-500 mb-4">Connect your social media accounts to start cross-posting</p>
                  <Button
                    onClick={() => setShowConnectModal(true)}
                    className="rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Connect Your First Platform
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {connections.map(conn => (
                    <div 
                      key={conn.connection_id}
                      className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-violet-300 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${conn.platform_color}20` }}
                        >
                          <PlatformIcon 
                            platform={conn.platform} 
                            className="w-6 h-6"
                            style={{ color: conn.platform_color }}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-gray-900 dark:text-white">{conn.platform_name}</h4>
                            {conn.is_active && (
                              <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{conn.account_username}</p>
                          <p className="text-xs text-gray-400">{conn.posts_count || 0} posts via HubPost</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => disconnectPlatform(conn.connection_id, conn.platform_name)}
                        className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Available Platforms */}
            <Card className="p-6 bg-white dark:bg-gray-800 rounded-3xl">
              <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Available Platforms</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(platforms).map(([key, platform]) => {
                  const isConnected = connectedPlatformIds.includes(key);
                  return (
                    <div 
                      key={key}
                      className={`p-4 rounded-xl border-2 text-center ${
                        isConnected 
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2"
                        style={{ backgroundColor: `${platform.color}20` }}
                      >
                        <PlatformIcon 
                          platform={key} 
                          className="w-6 h-6"
                          style={{ color: platform.color }}
                        />
                      </div>
                      <p className="font-semibold text-gray-900 dark:text-white">{platform.name}</p>
                      <p className="text-xs text-gray-500">{platform.max_chars} chars</p>
                      {isConnected ? (
                        <Badge className="mt-2 bg-green-100 text-green-700">Connected</Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setConnectingPlatform(key);
                            setShowConnectModal(true);
                          }}
                          className="mt-2 text-violet-600"
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-6">
            <Card className="p-6 bg-white dark:bg-gray-800 rounded-3xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-lg text-gray-900 dark:text-white">Cross-Post History</h3>
                <Button variant="ghost" onClick={fetchData} className="rounded-full">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              
              {history.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h4 className="font-bold text-xl text-gray-900 dark:text-white mb-2">No Cross-Posts Yet</h4>
                  <p className="text-gray-500">Your cross-posting history will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {history.map(post => (
                    <div 
                      key={post.crosspost_id}
                      className="p-4 rounded-xl border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <p className="text-gray-900 dark:text-white line-clamp-2">{post.original_content}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {new Date(post.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Badge className={post.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                          {post.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {post.platforms?.map(p => (
                          <div 
                            key={p}
                            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 dark:bg-gray-700"
                          >
                            <PlatformIcon platform={p} className="w-3 h-3" />
                            <span className="text-xs">{platforms[p]?.name || p}</span>
                            {post.results?.[p]?.status === 'success' && (
                              <CheckCircle2 className="w-3 h-3 text-green-500" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Connect Platform Modal */}
      <AnimatePresence>
        {showConnectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => {
              setShowConnectModal(false);
              setConnectingPlatform(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-lg p-6 shadow-2xl max-h-[80vh] overflow-y-auto"
              data-testid="connect-platform-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Connect Platform</h2>
                <button
                  onClick={() => {
                    setShowConnectModal(false);
                    setConnectingPlatform(null);
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {connectingPlatform ? (
                // Single platform connection form
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-violet-50 dark:bg-violet-900/20 rounded-xl">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${platforms[connectingPlatform]?.color}20` }}
                    >
                      <PlatformIcon 
                        platform={connectingPlatform} 
                        className="w-7 h-7"
                        style={{ color: platforms[connectingPlatform]?.color }}
                      />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-gray-900 dark:text-white">
                        {platforms[connectingPlatform]?.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Max {platforms[connectingPlatform]?.max_chars} characters
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      Full OAuth integration requires API keys. For now, enter your account details manually to simulate the connection.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold mb-2">Username/Handle</label>
                    <Input
                      value={manualConnect.username}
                      onChange={(e) => setManualConnect({ ...manualConnect, username: e.target.value })}
                      placeholder="@yourusername"
                      className="rounded-xl"
                      data-testid="platform-username-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold mb-2">Account Name (optional)</label>
                    <Input
                      value={manualConnect.accountName}
                      onChange={(e) => setManualConnect({ ...manualConnect, accountName: e.target.value })}
                      placeholder="My Business Account"
                      className="rounded-xl"
                      data-testid="platform-account-name-input"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setConnectingPlatform(null)}
                      className="flex-1 rounded-full"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={() => connectPlatform(connectingPlatform)}
                      className="flex-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white"
                      data-testid="confirm-connect-btn"
                    >
                      Connect {platforms[connectingPlatform]?.name}
                    </Button>
                  </div>
                </div>
              ) : (
                // Platform selection grid
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(platforms).map(([key, platform]) => {
                    const isConnected = connectedPlatformIds.includes(key);
                    return (
                      <button
                        key={key}
                        onClick={() => !isConnected && setConnectingPlatform(key)}
                        disabled={isConnected}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          isConnected 
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20 cursor-not-allowed' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-violet-400 cursor-pointer'
                        }`}
                        data-testid={`connect-${key}-btn`}
                      >
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-2"
                          style={{ backgroundColor: `${platform.color}20` }}
                        >
                          <PlatformIcon 
                            platform={key} 
                            className="w-5 h-5"
                            style={{ color: platform.color }}
                          />
                        </div>
                        <p className="font-semibold text-gray-900 dark:text-white">{platform.name}</p>
                        {isConnected ? (
                          <p className="text-xs text-green-600 mt-1">Already connected</p>
                        ) : (
                          <p className="text-xs text-gray-500 mt-1">Click to connect</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FloatingDock user={user} />
    </div>
  );
};

export default HubPost;
